import { Document } from "@langchain/core/documents";
import type { EmbeddingsInterface } from "@langchain/core/embeddings";
import type { DocumentInterface } from "@langchain/core/documents";
import { VectorStore } from "@langchain/core/vectorstores";

const IdColumnSymbol = Symbol("id");
const ContentColumnSymbol = Symbol("content");

type ColumnSymbol = typeof IdColumnSymbol | typeof ContentColumnSymbol;

declare type Value = unknown;
declare type RawValue = Value | Sql;

declare class Sql {
  strings: string[];

  constructor(
    rawStrings: ReadonlyArray<string>,
    rawValues: ReadonlyArray<RawValue>
  );
}

type PrismaNamespace = {
  ModelName: Record<string, string>;
  Sql: typeof Sql;
  raw: (sql: string) => Sql;
  join: (
    values: RawValue[],
    separator?: string,
    prefix?: string,
    suffix?: string
  ) => Sql;
  sql: (strings: ReadonlyArray<string>, ...values: RawValue[]) => Sql;
};

type PrismaClient = {
  $queryRaw<T = unknown>(
    query: TemplateStringsArray | Sql,
    // oxlint-disable-next-line typescript/no-explicit-any
    ...values: any[]
  ): Promise<T>;
  $executeRaw(
    query: TemplateStringsArray | Sql,
    // oxlint-disable-next-line typescript/no-explicit-any
    ...values: any[]
  ): // oxlint-disable-next-line typescript/no-explicit-any
  Promise<any>;

  // oxlint-disable-next-line typescript/no-explicit-any
  $transaction<P extends Promise<any>[]>(arg: [...P]): Promise<any>;
};

type ObjectIntersect<A, B> = {
  [P in keyof A & keyof B]: A[P] | B[P];
};

type ModelColumns<TModel extends Record<string, unknown>> = {
  [K in keyof TModel]?: true | ColumnSymbol;
};

export type PrismaSqlFilter<TModel extends Record<string, unknown>> = {
  [K in keyof TModel]?: {
    equals?: TModel[K];
    in?: TModel[K][];
    notIn?: TModel[K][];
    isNull?: TModel[K];
    isNotNull?: TModel[K];
    like?: TModel[K];
    lt?: TModel[K];
    lte?: TModel[K];
    gt?: TModel[K];
    gte?: TModel[K];
    not?: TModel[K];
  };
};

const OpMap = {
  equals: "=",
  in: "IN",
  notIn: "NOT IN",
  isNull: "IS NULL",
  isNotNull: "IS NOT NULL",
  like: "LIKE",
  lt: "<",
  lte: "<=",
  gt: ">",
  gte: ">=",
  not: "<>",
};

type SimilarityModel<
  TModel extends Record<string, unknown> = Record<string, unknown>,
  TColumns extends ModelColumns<TModel> = ModelColumns<TModel>,
> = Pick<TModel, keyof ObjectIntersect<TModel, TColumns>> & {
  _distance: number | null;
};

type DefaultPrismaVectorStore = PrismaVectorStore<
  Record<string, unknown>,
  string,
  ModelColumns<Record<string, unknown>>,
  PrismaSqlFilter<Record<string, unknown>>
>;

/**
 * Configuration for column types to enable proper type casting in SQL queries.
 * This is particularly important for columns that require explicit casting,
 * such as UUID columns in PostgreSQL.
 */
export interface ColumnTypeConfig {
  [key: string]: "uuid" | "text" | "integer" | "bigint" | "jsonb";
}

/**
 * A specific implementation of the VectorStore class that is designed to
 * work with Prisma. It provides methods for adding models, documents, and
 * vectors, as well as for performing similarity searches.
 */
export class PrismaVectorStore<
  TModel extends Record<string, unknown>,
  TModelName extends string,
  TSelectModel extends ModelColumns<TModel>,
  TFilterModel extends PrismaSqlFilter<TModel>,
> extends VectorStore {
  declare FilterType: TFilterModel;

  protected tableName: string;

  protected vectorColumnName: string;

  protected selectColumns: string[];

  filter?: TFilterModel;

  idColumn: keyof TModel & string;

  contentColumn: keyof TModel & string;

  protected columnTypes?: ColumnTypeConfig;

  /**
   * When true, addDocuments uses INSERT statements to create new records.
   * When false (default), addDocuments uses UPDATE statements to update existing records by ID.
   * Set to true when using with ParentDocumentRetriever or when documents don't pre-exist in the database.
   */
  protected useInsert: boolean;

  static IdColumn: typeof IdColumnSymbol = IdColumnSymbol;

  static ContentColumn: typeof ContentColumnSymbol = ContentColumnSymbol;

  protected db: PrismaClient;

  protected Prisma: PrismaNamespace;

  _vectorstoreType(): string {
    return "prisma";
  }

  constructor(
    embeddings: EmbeddingsInterface,
    config: {
      db: PrismaClient;
      prisma: PrismaNamespace;
      tableName: TModelName;
      vectorColumnName: string;
      columns: TSelectModel;
      filter?: TFilterModel;
      columnTypes?: ColumnTypeConfig;
      /**
       * When true, addDocuments uses INSERT statements to create new records.
       * When false (default), addDocuments uses UPDATE statements to update existing records by ID.
       * Set to true when using with ParentDocumentRetriever or when documents don't pre-exist in the database.
       */
      useInsert?: boolean;
    }
  ) {
    super(embeddings, {});

    this.Prisma = config.prisma;
    this.db = config.db;

    const entries = Object.entries(config.columns);
    const idColumn = entries.find((i) => i[1] === IdColumnSymbol)?.[0];
    const contentColumn = entries.find(
      (i) => i[1] === ContentColumnSymbol
    )?.[0];

    if (idColumn == null) throw new Error("Missing ID column");
    if (contentColumn == null) throw new Error("Missing content column");

    this.idColumn = idColumn;
    this.contentColumn = contentColumn;

    this.tableName = config.tableName;
    this.vectorColumnName = config.vectorColumnName;
    this.columnTypes = config.columnTypes;
    this.useInsert = config.useInsert ?? false;

    this.selectColumns = entries
      .map(([key, alias]) => (alias && key) || null)
      .filter((x): x is string => !!x);

    if (config.filter) {
      this.filter = config.filter;
    }
  }

  /**
   * Creates a new PrismaVectorStore with the specified model.
   * @param db The PrismaClient instance.
   * @returns An object with create, fromTexts, and fromDocuments methods.
   */
  static withModel<TModel extends Record<string, unknown>>(db: PrismaClient) {
    function create<
      TPrisma extends PrismaNamespace,
      TColumns extends ModelColumns<TModel>,
      TFilters extends PrismaSqlFilter<TModel>,
    >(
      embeddings: EmbeddingsInterface,
      config: {
        prisma: TPrisma;
        tableName: keyof TPrisma["ModelName"] & string;
        vectorColumnName: string;
        columns: TColumns;
        filter?: TFilters;
        columnTypes?: ColumnTypeConfig;
        useInsert?: boolean;
      }
    ) {
      type ModelName = keyof TPrisma["ModelName"] & string;
      return new PrismaVectorStore<TModel, ModelName, TColumns, TFilters>(
        embeddings,
        { ...config, db }
      );
    }

    async function fromTexts<
      TPrisma extends PrismaNamespace,
      TColumns extends ModelColumns<TModel>,
    >(
      texts: string[],
      metadatas: TModel[],
      embeddings: EmbeddingsInterface,
      dbConfig: {
        prisma: TPrisma;
        tableName: keyof TPrisma["ModelName"] & string;
        vectorColumnName: string;
        columns: TColumns;
        columnTypes?: ColumnTypeConfig;
        useInsert?: boolean;
      }
    ) {
      const docs: Document[] = [];
      for (let i = 0; i < texts.length; i += 1) {
        const metadata = Array.isArray(metadatas) ? metadatas[i] : metadatas;
        const newDoc = new Document({
          pageContent: texts[i],
          metadata,
        });
        docs.push(newDoc);
      }

      return PrismaVectorStore.fromDocuments(docs, embeddings, {
        ...dbConfig,
        db,
      });
    }

    async function fromDocuments<
      TPrisma extends PrismaNamespace,
      TColumns extends ModelColumns<TModel>,
      TFilters extends PrismaSqlFilter<TModel>,
    >(
      docs: Document<TModel>[],
      embeddings: EmbeddingsInterface,
      dbConfig: {
        prisma: TPrisma;
        tableName: keyof TPrisma["ModelName"] & string;
        vectorColumnName: string;
        columns: TColumns;
        columnTypes?: ColumnTypeConfig;
        useInsert?: boolean;
      }
    ) {
      type ModelName = keyof TPrisma["ModelName"] & string;
      const instance = new PrismaVectorStore<
        TModel,
        ModelName,
        TColumns,
        TFilters
      >(embeddings, { ...dbConfig, db });
      await instance.addDocuments(docs);
      return instance;
    }

    return { create, fromTexts, fromDocuments };
  }

  /**
   * Adds the specified models to the store.
   * @param models The models to add.
   * @returns A promise that resolves when the models have been added.
   */
  async addModels(models: TModel[]) {
    return this.addDocuments(
      models.map((metadata) => {
        const pageContent = metadata[this.contentColumn];
        if (typeof pageContent !== "string")
          throw new Error("Content column must be a string");
        return new Document({ pageContent, metadata });
      })
    );
  }

  /**
   * Adds the specified documents to the store.
   * @param documents The documents to add.
   * @returns A promise that resolves when the documents have been added.
   */
  async addDocuments(documents: Document<TModel>[]) {
    const texts = documents.map(({ pageContent }) => pageContent);
    const vectors = await this.embeddings.embedDocuments(texts);

    if (this.useInsert) {
      return this.addDocumentsWithVectors(vectors, documents);
    }
    return this.addVectors(vectors, documents);
  }

  /**
   * Adds the specified vectors to the store.
   * @param vectors The vectors to add.
   * @param documents The documents associated with the vectors.
   * @returns A promise that resolves when the vectors have been added.
   */
  async addVectors(vectors: number[][], documents: Document<TModel>[]) {
    // table name, column name cannot be parametrised
    // these fields are thus not escaped by Prisma and can be dangerous if user input is used
    const idColumnRaw = this.Prisma.raw(`"${this.idColumn}"`);
    const tableNameRaw = this.Prisma.raw(`"${this.tableName}"`);
    const vectorColumnRaw = this.Prisma.raw(`"${this.vectorColumnName}"`);

    await this.db.$transaction(
      vectors.map((vector, idx) => {
        const idValue = documents[idx].metadata[this.idColumn];
        const columnType = this.columnTypes?.[this.idColumn];

        // Apply type casting based on configured column type
        let whereClause;
        if (columnType === "uuid") {
          whereClause = this.Prisma.sql`${idColumnRaw} = ${idValue}::uuid`;
        } else if (columnType === "integer") {
          whereClause = this.Prisma.sql`${idColumnRaw} = ${idValue}::integer`;
        } else if (columnType === "bigint") {
          whereClause = this.Prisma.sql`${idColumnRaw} = ${idValue}::bigint`;
        } else {
          // Default behavior for backward compatibility
          whereClause = this.Prisma.sql`${idColumnRaw} = ${idValue}`;
        }

        return this.db.$executeRaw(
          this.Prisma.sql`UPDATE ${tableNameRaw}
            SET ${vectorColumnRaw} = ${`[${vector.join(",")}]`}::vector
            WHERE ${whereClause}
          `
        );
      })
    );
  }

  /**
   * Adds documents with their corresponding vectors to the store using INSERT statements.
   * This method ensures documents are created if they don't exist, making it compatible
   * with ParentDocumentRetriever which creates new child documents.
   * @param vectors The vectors to add.
   * @param documents The documents associated with the vectors.
   * @returns A promise that resolves when the documents have been added.
   */
  async addDocumentsWithVectors(
    vectors: number[][],
    documents: Document<TModel>[]
  ) {
    // table name, column name cannot be parametrised
    // these fields are thus not escaped by Prisma and can be dangerous if user input is used
    const tableNameRaw = this.Prisma.raw(`"${this.tableName}"`);
    const vectorColumnRaw = this.Prisma.raw(`"${this.vectorColumnName}"`);

    // Build column names for INSERT statement
    const columnNames = this.selectColumns.map((col) =>
      this.Prisma.raw(`"${col}"`)
    );
    const allColumns = [...columnNames, vectorColumnRaw];

    await this.db.$transaction(
      vectors.map((vector, idx) => {
        const document = documents[idx];
        const vectorString = `[${vector.join(",")}]`;

        // Build values for each column
        const columnValues = this.selectColumns.map((col) => {
          if (col === this.contentColumn) {
            return document.pageContent;
          }
          return document.metadata[col];
        });

        // Add vector as the last value
        const allValues = [
          ...columnValues,
          this.Prisma.sql`${vectorString}::vector`,
        ];

        return this.db.$executeRaw(
          this.Prisma.sql`
            INSERT INTO ${tableNameRaw} (${this.Prisma.join(allColumns, ", ")})
            VALUES (${this.Prisma.join(allValues, ", ")})
          `
        );
      })
    );
  }

  /**
   * Performs a similarity search with the specified query.
   * @param query The query to use for the similarity search.
   * @param k The number of results to return.
   * @param _filter The filter to apply to the results.
   * @param _callbacks The callbacks to use during the search.
   * @returns A promise that resolves with the search results.
   */
  async similaritySearch(
    query: string,
    k = 4,
    filter: this["FilterType"] | undefined = undefined
  ): Promise<Document<SimilarityModel<TModel, TSelectModel>>[]> {
    const results = await this.similaritySearchVectorWithScore(
      await this.embeddings.embedQuery(query),
      k,
      filter
    );

    return results.map((result) => result[0]);
  }

  /**
   * Performs a similarity search with the specified query and returns the
   * results along with their scores.
   * @param query The query to use for the similarity search.
   * @param k The number of results to return.
   * @param filter The filter to apply to the results.
   * @param _callbacks The callbacks to use during the search.
   * @returns A promise that resolves with the search results and their scores.
   */
  async similaritySearchWithScore(
    query: string,
    k?: number,
    filter?: this["FilterType"]
    // oxlint-disable-next-line typescript/no-explicit-any
  ): Promise<[DocumentInterface<Record<string, any>>, number][]> {
    return super.similaritySearchWithScore(query, k, filter);
  }

  /**
   * Performs a similarity search with the specified vector and returns the
   * results along with their scores.
   * @param query The vector to use for the similarity search.
   * @param k The number of results to return.
   * @param filter The filter to apply to the results.
   * @returns A promise that resolves with the search results and their scores.
   */
  async similaritySearchVectorWithScore(
    query: number[],
    k: number,
    filter?: this["FilterType"]
  ): Promise<[Document<SimilarityModel<TModel, TSelectModel>>, number][]> {
    // table name, column names cannot be parametrised
    // these fields are thus not escaped by Prisma and can be dangerous if user input is used
    const vectorColumnRaw = this.Prisma.raw(`"${this.vectorColumnName}"`);
    const tableNameRaw = this.Prisma.raw(`"${this.tableName}"`);
    const selectRaw = this.Prisma.raw(
      this.selectColumns.map((x) => `"${x}"`).join(", ")
    );

    const vector = `[${query.join(",")}]`;
    const articles = await this.db.$queryRaw<
      Array<SimilarityModel<TModel, TSelectModel>>
    >(
      this.Prisma.join(
        [
          this.Prisma.sql`
            SELECT ${selectRaw}, ${vectorColumnRaw} <=> ${vector}::vector as "_distance"
            FROM ${tableNameRaw}
          `,
          this.buildSqlFilterStr(filter ?? this.filter),
          this.Prisma.sql`
            ORDER BY "_distance" ASC
            LIMIT ${k};
          `,
        ].filter((x) => x != null),
        ""
      )
    );

    const results: [Document<SimilarityModel<TModel, TSelectModel>>, number][] =
      [];
    for (const article of articles) {
      if (article._distance != null && article[this.contentColumn] != null) {
        results.push([
          new Document({
            pageContent: article[this.contentColumn] as string,
            metadata: article,
          }),
          article._distance,
        ]);
      }
    }

    return results;
  }

  buildSqlFilterStr(filter?: this["FilterType"]) {
    if (filter == null) return null;
    return this.Prisma.join(
      Object.entries(filter).flatMap(([key, ops]) =>
        Object.entries(ops).map(([opName, value]) => {
          // column name, operators cannot be parametrised
          // these fields are thus not escaped by Prisma and can be dangerous if user input is used
          const opNameKey = opName as keyof typeof OpMap;
          const colRaw = this.Prisma.raw(`"${key}"`);
          const opRaw = this.Prisma.raw(OpMap[opNameKey]);
          const columnType = this.columnTypes?.[key];

          switch (OpMap[opNameKey]) {
            case OpMap.notIn:
            case OpMap.in: {
              if (!Array.isArray(value)) {
                throw new Error(
                  `Invalid filter: IN operator requires an array. Received: ${JSON.stringify(
                    value,
                    null,
                    2
                  )}`
                );
              }

              if (value.length === 0) {
                const isInOperator = OpMap[opNameKey] === OpMap.in;

                // For empty arrays:
                // - IN () should return FALSE (nothing can be in an empty set)
                // - NOT IN () should return TRUE (everything is not in an empty set)
                return this.Prisma.sql`${!isInOperator}`;
              }

              // Apply type casting for IN/NOT IN operators based on column type
              if (columnType === "uuid") {
                const castedValues = value.map(
                  (v) => this.Prisma.sql`${v}::uuid`
                );
                return this.Prisma.sql`${colRaw} ${opRaw} (${this.Prisma.join(
                  castedValues
                )})`;
              } else if (columnType === "integer") {
                const castedValues = value.map(
                  (v) => this.Prisma.sql`${v}::integer`
                );
                return this.Prisma.sql`${colRaw} ${opRaw} (${this.Prisma.join(
                  castedValues
                )})`;
              } else if (columnType === "bigint") {
                const castedValues = value.map(
                  (v) => this.Prisma.sql`${v}::bigint`
                );
                return this.Prisma.sql`${colRaw} ${opRaw} (${this.Prisma.join(
                  castedValues
                )})`;
              } else if (columnType === "jsonb") {
                const castedValues = value.map((v) => {
                  const jsonValue =
                    typeof v === "object" ? JSON.stringify(v) : v;
                  return this.Prisma.sql`${jsonValue}::jsonb`;
                });
                return this.Prisma.sql`${colRaw} ${opRaw} (${this.Prisma.join(
                  castedValues
                )})`;
              }

              return this.Prisma.sql`${colRaw} ${opRaw} (${this.Prisma.join(
                value
              )})`;
            }
            case OpMap.isNull:
            case OpMap.isNotNull:
              return this.Prisma.sql`${colRaw} ${opRaw}`;
            default:
              // Apply proper type casting based on column type
              if (columnType === "uuid") {
                return this.Prisma.sql`${colRaw} ${opRaw} ${value}::uuid`;
              } else if (columnType === "integer") {
                return this.Prisma.sql`${colRaw} ${opRaw} ${value}::integer`;
              } else if (columnType === "bigint") {
                return this.Prisma.sql`${colRaw} ${opRaw} ${value}::bigint`;
              } else if (columnType === "jsonb") {
                // For JSONB, cast the value to JSON string then to jsonb
                const jsonValue =
                  typeof value === "object" ? JSON.stringify(value) : value;
                return this.Prisma.sql`${colRaw} ${opRaw} ${jsonValue}::jsonb`;
              } else if (columnType) {
                // For other specified types, apply the cast
                return this.Prisma
                  .sql`${colRaw} ${opRaw} ${value}::${this.Prisma.raw(
                  columnType
                )}`;
              } else {
                // Default behavior for backward compatibility - cast column to text
                return this.Prisma.sql`${colRaw}::text ${opRaw} ${value}`;
              }
          }
        })
      ),
      " AND ",
      " WHERE "
    );
  }

  /**
   * Creates a new PrismaVectorStore from the specified texts.
   * @param texts The texts to use to create the store.
   * @param metadatas The metadata for the texts.
   * @param embeddings The embeddings to use.
   * @param dbConfig The database configuration.
   * @returns A promise that resolves with the new PrismaVectorStore.
   */
  static async fromTexts(
    texts: string[],
    metadatas: object[],
    embeddings: EmbeddingsInterface,
    dbConfig: {
      db: PrismaClient;
      prisma: PrismaNamespace;
      tableName: string;
      vectorColumnName: string;
      columns: ModelColumns<Record<string, unknown>>;
      columnTypes?: ColumnTypeConfig;
      useInsert?: boolean;
    }
  ): Promise<DefaultPrismaVectorStore> {
    const docs: Document[] = [];
    for (let i = 0; i < texts.length; i += 1) {
      const metadata = Array.isArray(metadatas) ? metadatas[i] : metadatas;
      const newDoc = new Document({
        pageContent: texts[i],
        metadata,
      });
      docs.push(newDoc);
    }

    return PrismaVectorStore.fromDocuments(docs, embeddings, dbConfig);
  }

  /**
   * Creates a new PrismaVectorStore from the specified documents.
   * @param docs The documents to use to create the store.
   * @param embeddings The embeddings to use.
   * @param dbConfig The database configuration.
   * @returns A promise that resolves with the new PrismaVectorStore.
   */
  static async fromDocuments(
    docs: Document[],
    embeddings: EmbeddingsInterface,
    dbConfig: {
      db: PrismaClient;
      prisma: PrismaNamespace;
      tableName: string;
      vectorColumnName: string;
      columns: ModelColumns<Record<string, unknown>>;
      columnTypes?: ColumnTypeConfig;
      useInsert?: boolean;
    }
  ): Promise<DefaultPrismaVectorStore> {
    const instance = new PrismaVectorStore(embeddings, dbConfig);
    await instance.addDocuments(docs);
    return instance;
  }
}
